import { CommonModule } from '@angular/common';
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  input,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { PasswordModule } from 'primeng/password';
import { ShFormControl } from '../form-control/form-control.base';
import { primeSize } from '../models';

/**
 * Password input with the show/hide toggle (`toggleMask`, on by default) and
 * no strength meter. `icon` puts a Font Awesome icon in front.
 *
 *   <sh-password [(value)]="password" icon="fa-solid fa-lock" />
 */
@Component({
  selector: 'sh-password',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    PasswordModule,
    IconFieldModule,
    InputIconModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShPasswordComponent),
      multi: true,
    },
  ],
  host: { class: 'sh-control', '[class.sh-invalid]': 'isInvalid()' },
  template: `
    <p-iconfield>
      <p-inputicon *ngIf="icon()" [styleClass]="icon()!"></p-inputicon>
      <p-password
        [ngModel]="value()"
        (ngModelChange)="commit($event)"
        [ngModelOptions]="{ standalone: true }"
        [inputId]="controlId()"
        [placeholder]="placeholder() | translate"
        [ariaLabel]="ariaLabel() | translate"
        [feedback]="false"
        [toggleMask]="toggleMask()"
        [disabled]="isDisabled()"
        [size]="pSize()"
        [fluid]="true"
        (onBlur)="markTouched()"
      ></p-password>
    </p-iconfield>
  `,
})
export class ShPasswordComponent extends ShFormControl<string> {
  readonly toggleMask = input(true, { transform: booleanAttribute });
  /** Font Awesome classes, shown in front of the text. */
  readonly icon = input<string>();

  protected readonly pSize = computed(
    () => primeSize(this.size()) as 'small' | 'large'
  );
}
